/**
 * Constantes para el módulo de cotizaciones
 */
export const QUOTE_CONSTANTS = {
  // Tipos de cotización
  QUOTE_TYPES: {
    FULL: 1,
    LEY: 2,
    FLOTILLA: 3,
    MOST_TOTAL_LOSS_LAW: 4
  } as const,

  // Códigos de cobertura estándar
  COVERAGE_CODES: {
    LESIONES_MUERTE_1_PERSONA: 1,
    LESIONES_MUERTE_MAS_1_PERSONA: 2,
    DANOS_PROPIEDAD_AJENA: 3,
    LESIONES_MUERTE_1_PASAJERO: 4,
    LESIONES_MUERTE_MAS_1_PASAJERO: 5
  } as const,

  // Códigos de coberturas adicionales (códigos REALES del sistema legacy)
  ADDITIONAL_COVERAGE_CODES: {
    DAMAGE_PARTS: 488,             // Daños en Partes o Piezas por Desigualdad del Terreno
    DAMAGE_RIMS: 489,              // Daños en Aros y Gomas por Desigualdad del Terreno
    THEFT_PARTS: 491,              // Robo o hurto de Partes Internas y Externas
    NO_DEPRECIATION: 492,          // No Depreciación en Pérdida total
    WATER_DAMAGE: 493,             // Daños por Agua e Inundación
    UNDECLARED_ROADS: 494,         // Caminos No Declarados
    ANIMAL_DAMAGE: 495             // Daños provocados por animales
  } as const,

  // Códigos de coberturas opcionales (códigos REALES del sistema legacy)
  // Fuente: QuoteVehicle3.aspx.vb constantes L14-33
  OPTIONAL_COVERAGE_CODES: {
    CAA: 465,                      // Centro del Automovilista (CAA) - código principal
    CAA_ALT: 468,                  // Centro del Automovilista (CAA) - código alternativo (Centro_Automovilista_CAA1 en legacy)
    FUNERAL_ASSISTANCE_BASIC: 527, // Asistencia Funeraria La Colonial BASIC
    CASA_DEL_CONDUCTOR: 346,       // Casa del Conductor
    AIR_AMBULANCE: 419,            // Aeroambulancia - Auto Alert Plus 1 (exclusión mutua con AUTO_ALERT_PLUS)
    AUTO_ALERT_PLUS: 424,          // Auto Alert Plus 2 (exclusión mutua con AIR_AMBULANCE)
    ROADSIDE_ASSISTANCE: 455,      // Asistencia Vial Full
    ROADSIDE_ASSISTANCE_LEY: 454,  // Asistencia Vial Ley
    ROADSIDE_ASSISTANCE_CAMION: 456,// Asistencia Vial Camiones
    MEDICAL_EXPENSES: 498,         // Auto Relax - Gastos Médicos Hasta RD$1,000,000.00
    FUNERAL_ASSISTANCE: 528        // Asistencia Funeraria La Colonial DIAMOND
  } as const,

  // Códigos de coberturas de plan (Opciones del Plan)
  PLAN_COVERAGE_CODES: {
    RISK_DRIVER: 301,              // Riesgo Conductor
    RENTAL_CAR_START: 499,         // Renta Auto - código inicial del rango
    RENTAL_CAR_END: 513,           // Renta Auto - código final del rango
    BAIL_BOND: 183,                // Fianza Judicial
    AUTO_EXCESS: 544,               // RC Auto Exceso
    LABORERS_ASSISTANTS: 303        // Peones y Ayudantes
  } as const,

  // Límites por defecto para coberturas opcionales
  OPTIONAL_COVERAGE_LIMITS: {
    MEDICAL_EXPENSES: 1000000      // Límite fijo para Gastos Médicos
  } as const,

  // Valores por defecto
  DEFAULTS: {
    QUOTE_TYPE: 1,
    EXPAND_COVERAGE: 500000,
    DRIVER_RISK: 100000,
    VEHICLE_CYLINDERS: 4,
    VEHICLE_PASSENGERS: 5,
    VEHICLE_CC: 1800,
    FUEL_TYPE: 1,
    VEHICLE_COLOR: 1,
    COUNT_VEHICLE: 1
  } as const,

  // Límites de validación
  VALIDATION: {
    MIN_YEAR: 1900,
    MIN_VEHICLE_VALUE: 0.01,
    MIN_PERSONALITY_CODE: 1,
    MAX_QUOTE_TYPE: 4
  } as const,

  // Descripciones de coberturas
  COVERAGE_DESCRIPTIONS: {
    1: 'Lesiones o Muerte 1 Persona',
    2: 'Lesiones o Muerte Más de 1 Persona',
    3: 'Daños a la Propiedad Ajena',
    4: 'Lesiones o Muerte 1 Pasajero',
    5: 'Lesiones o Muerte Más de 1 Pasajero'
  } as const,

  // Estados de cotización
  QUOTE_STATUS: {
    DRAFT: 1,        // Borrador/En proceso/Pendiente de emisión
    ISSUED: 3        // Emitida
  } as const,

  // Constantes para flotilla
  FLEET: {
    CALCULATION_DELAY_MS: 500,
    DEFAULT_FUEL_TYPE: 1,
    DEFAULT_VEHICLE_COLOR: 1,
    DEFAULT_PASSENGERS: 5,
    DEFAULT_COUNT_VEHICLE: 1
  } as const
} as const;

/**
 * Convierte a número un valor que puede venir como string del formulario o del API.
 */
function toNumeric(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  return isNaN(num) ? 0 : num;
}

/**
 * ¿Esta unidad es de Ley?
 *
 * PARIDAD LEGACY: el usuario sólo elige Full o Ley; "flotilla" no es un tipo que se elija, sino
 * algo que ocurre al agregar unidades. En el legacy el motor recibe siempre el tipo real
 * (Full/Ley) más una bandera AddFlotilla aparte, y el tipo 3 aparece únicamente en el encabezado
 * guardado — por eso TODAS las flotillas quedan con QuoteTypeCode = 3, sean Full o Ley.
 *
 * Entonces, en una flotilla la naturaleza Ley no está en el encabezado sino en cada unidad: las
 * de Ley se guardan sin valor asegurado y sin plan. Se exigen ambas condiciones a propósito, para
 * que las unidades ambiguas del histórico (valor 0 con plan > 0) se sigan tratando como Full,
 * igual que hoy.
 */
export function isLeyUnit(quoteTypeCode: unknown, vehicleValue: unknown, planId: unknown): boolean {
  const type = toNumeric(quoteTypeCode);
  if (type === QUOTE_CONSTANTS.QUOTE_TYPES.LEY) return true;
  if (type === QUOTE_CONSTANTS.QUOTE_TYPES.FLOTILLA) {
    return toNumeric(vehicleValue) <= 0 && toNumeric(planId) <= 0;
  }
  return false;
}

/**
 * Tipo de cobertura con el que se TRABAJA una unidad: el que el usuario tiene elegido en
 * "Tipo de Cotización" al agregarla o editarla.
 *
 * Flotilla (3) no es un tipo de cobertura sino el tipo del ENCABEZADO, así que si llega hasta aquí
 * se cae a Full, igual que hace el backend. Lo mismo con un valor vacío o inválido.
 *
 * Es el único lugar donde se decide esto: lo usan el builder de unidades de flotilla, el mapper de
 * requests y el componente al restaurar una unidad, para que los tres coincidan siempre.
 */
export function resolveUnitQuoteType(quoteTypeCode: unknown): number {
  const type = toNumeric(quoteTypeCode);
  if (type <= 0 || type === QUOTE_CONSTANTS.QUOTE_TYPES.FLOTILLA) {
    return QUOTE_CONSTANTS.QUOTE_TYPES.FULL;
  }
  return type;
}

/**
 * Tipo con el que se debe TRABAJAR una cotización recién cargada del API.
 *
 * El encabezado guardado dice "Flotilla" (3) sin distinguir Full de Ley. Esta función devuelve el
 * tipo real (Full o Ley) mirando las unidades, de modo que el formulario quede exactamente como si
 * el usuario acabara de elegirlo: así toda la pantalla y la construcción del request siguen
 * funcionando sin cambios, y una flotilla Ley deja de tratarse como Full al reabrirla.
 *
 * Los tipos que no son flotilla se devuelven tal cual.
 */
export function resolveWorkingQuoteType(quoteTypeCode: unknown, vehicles: any[] | null | undefined): number {
  const type = toNumeric(quoteTypeCode);
  if (type !== QUOTE_CONSTANTS.QUOTE_TYPES.FLOTILLA) {
    return type;
  }

  const units = vehicles ?? [];
  if (units.length === 0) {
    return QUOTE_CONSTANTS.QUOTE_TYPES.FULL;
  }

  // Sólo es una flotilla Ley si TODAS sus unidades lo son.
  const allLey = units.every(v =>
    isLeyUnit(
      QUOTE_CONSTANTS.QUOTE_TYPES.FLOTILLA,
      v?.vehicleValue ?? v?.VehicleValue,
      v?.idQuoteIntelligentPlan ?? v?.IdQuoteIntelligentPlan ?? v?.IDQuoteIntelligentPlan
    )
  );

  return allLey ? QUOTE_CONSTANTS.QUOTE_TYPES.LEY : QUOTE_CONSTANTS.QUOTE_TYPES.FULL;
}

/**
 * Códigos con los que el motor (QuoteFactory) persiste las coberturas estándar en
 * QuoteIntelligentCoverage, mapeados a los códigos estándar (1-5) que usa el formulario.
 *
 * FUENTE DE VERDAD: SISCOLONIAL.dbo.Coverage
 *   238 = Lesiones o Muerte 1 Persona         → 1
 *   240 = Lesiones o Muerte Más de 1 Persona  → 2
 *   141 = Daños a la Propiedad Ajena          → 3
 *   241 = Lesiones o Muerte 1 Pasajero        → 4
 *   239 = Lesiones o Muerte Más de 1 Pasajero → 5
 *
 * Este es el ÚNICO lugar donde vive el mapeo. Antes estaba duplicado (y con los pasajeros
 * invertidos) en el backend y en el mapeo de flotilla, por lo que la lista de coberturas
 * cambiaba de orden y de etiquetas entre la pantalla de cotización y la de reapertura.
 */
export const DB_TO_STANDARD_COVERAGE_CODE: Readonly<Record<number, number>> = {
  238: 1,
  240: 2,
  141: 3,
  241: 4,
  239: 5
};

/**
 * Normaliza un código de cobertura estándar de BD (141/238-241) al código 1-5 del formulario.
 * Los códigos que no son estándar (o que ya vienen normalizados) se devuelven sin cambios.
 */
export function toStandardCoverageCode(code: number): number {
  return DB_TO_STANDARD_COVERAGE_CODE[code] ?? code;
}

/**
 * ¿El código corresponde a una cobertura estándar (RC), en cualquiera de sus dos formas
 * (código de BD 141/238-241 o código normalizado 1-5)?
 */
export function isStandardCoverageCode(code: number): boolean {
  return (code >= 1 && code <= 5) || code in DB_TO_STANDARD_COVERAGE_CODE;
}

/**
 * Obtiene la descripción de una cobertura por su código
 */
export function getCoverageDescription(code: number): string {
  return QUOTE_CONSTANTS.COVERAGE_DESCRIPTIONS[code as keyof typeof QUOTE_CONSTANTS.COVERAGE_DESCRIPTIONS]
    || `Cobertura ${code}`;
}

/**
 * Obtiene la etiqueta del estado de una cotización
 * @param status - Estado de la cotización (1 = Borrador, 3 = Emitida, null/undefined = Borrador)
 * @returns Etiqueta del estado
 */
export function getQuoteStatusLabel(status: number | null | undefined): string {
  if (status === null || status === undefined || status === QUOTE_CONSTANTS.QUOTE_STATUS.DRAFT) {
    return 'Borrador';
  }
  if (status === QUOTE_CONSTANTS.QUOTE_STATUS.ISSUED) {
    return 'Emitida';
  }
  // Para cualquier otro valor, retornar el número como fallback
  return `Estado ${status}`;
}

/**
 * Obtiene la clase CSS del badge para el estado de una cotización
 * @param status - Estado de la cotización (1 = Borrador, 3 = Emitida, null/undefined = Borrador)
 * @returns Clase CSS del badge (badge-warning para borrador, badge-success para emitida)
 */
export function getQuoteStatusBadgeClass(status: number | null | undefined): string {
  if (status === null || status === undefined || status === QUOTE_CONSTANTS.QUOTE_STATUS.DRAFT) {
    return 'badge-warning'; // Amarillo/naranja para borrador
  }
  if (status === QUOTE_CONSTANTS.QUOTE_STATUS.ISSUED) {
    return 'badge-success'; // Verde para emitida
  }
  // Para cualquier otro valor (estado desconocido), usar fondo --colonial-gray (#434143) con letras blancas
  return 'bg-colonial-gray border-colonial-gray text-white';
}

/**
 * Obtiene el código de cobertura de asistencia vial basado en categoría y tipo de cotización
 */
export function getRoadsideAssistanceCode(categoryCode: number, quoteTypeCode: number): number {
  const isHeavy = Number(categoryCode) === 4;
  const isLey = Number(quoteTypeCode) === 2;
  
  if (isHeavy) {
    return QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.ROADSIDE_ASSISTANCE_CAMION; // 456
  } else if (isLey) {
    return QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.ROADSIDE_ASSISTANCE_LEY; // 454
  } else {
    return QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.ROADSIDE_ASSISTANCE; // 455
  }
}

/**
 * Código de Aeroambulancia que corresponde según el tipo de cotización.
 *
 *   Ley  → 424 "Auto Alert Plus 2"
 *   Full → 419 "Auto Alert Plus 1"
 *
 * Verificado contra el histórico del legacy, que nunca se desvía: 46 cotizaciones Full con 419 y
 * 53 de Ley con 424, sin un solo caso cruzado. El legacy no elige el código — llena su grid de
 * opcionales con GetVehicleSubTypeCoverage(subtipo, Other, 0, tipoCotización), filtrado POR TIPO,
 * y manda el que le entrega el catálogo.
 *
 * Aquí se elegía tomando el primero de [419, 424] que no estuviera excluido, sin mirar el tipo.
 * En los 49 subtipos que tienen las dos configuradas eso mandaba siempre 419, incluso en Ley, y la
 * pantalla mostraba "Auto Alert Plus 2" mientras se guardaba Plus 1 (caso 2218545 vs 1516159).
 * Es el mismo tratamiento por tipo que ya tiene Asistencia Vial en getRoadsideAssistanceCode.
 */
export function getAirAmbulanceCode(quoteTypeCode: number): number {
  return Number(quoteTypeCode) === QUOTE_CONSTANTS.QUOTE_TYPES.LEY
    ? QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.AUTO_ALERT_PLUS   // 424
    : QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.AIR_AMBULANCE;    // 419
}

/**
 * Obtiene la clave del control de formulario correspondiente a un código de cobertura opcional
 */
export function getOptionalCoverageFormKey(code: number, quoteTypeCode: number, categoryCode: number): string {
  if (code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.CAA || code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.CAA_ALT) {
    return 'coverageCAA';
  }
  if (code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.CASA_DEL_CONDUCTOR) {
    return 'coverageDriverHouse';
  }
  if (code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.AIR_AMBULANCE || code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.AUTO_ALERT_PLUS) {
    return 'coverageAirAmbulance';
  }
  if (
    code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.ROADSIDE_ASSISTANCE ||
    code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.ROADSIDE_ASSISTANCE_LEY ||
    code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.ROADSIDE_ASSISTANCE_CAMION
  ) {
    return 'coverageRoadsideAssistance';
  }
  if (code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.MEDICAL_EXPENSES) {
    return 'coverageMedicalExpenses';
  }
  if (code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.FUNERAL_ASSISTANCE) {
    return 'coverageFuneralAssistance';
  }
  if (code === QUOTE_CONSTANTS.OPTIONAL_COVERAGE_CODES.FUNERAL_ASSISTANCE_BASIC) {
    return 'coverageFuneralAssistanceBasic';
  }
  return '';
}
